import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import type { PsUiSize } from '../../types';

// defined types
export type ButtonVariant = 'filled' | 'outline' | 'soft' | 'ghost';
export type ButtonType = 'button' | 'submit' | 'reset';

export type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'light'
  | 'dark';

// define mappers, with type safety
const PREFIX_MAP: Record<ButtonVariant, string> = {
  filled: 'btn',
  outline: 'btn-outline',
  soft: 'btn-soft',
  ghost: 'btn-ghost',
};

const SIZE_MAP: Record<PsUiSize, string> = {
  sm: 'btn-sm',
  md: 'btn-md',
  lg: 'btn-lg',
};

@Component({
  selector: 'ps-button',
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonComponent {
  public readonly type = input<ButtonType>('button');
  public readonly variant = input<ButtonVariant>('filled');
  public readonly color = input<ButtonColor>('primary');
  public readonly size = input<PsUiSize>('md');
  public readonly disabled = input<boolean>(false);
  public readonly id = input<string>();
  public readonly ariaLabel = input<string | null>();
  public readonly ariaExpanded = input<boolean | null>();
  public readonly ariaControls = input<string | null>();
  public readonly clicked = output<MouseEvent>();

  public variantClass = computed(
    () => `${PREFIX_MAP[this.variant()]}-${this.color()}`,
  );

  public classes = computed(() =>
    [SIZE_MAP[this.size()], this.variantClass()].join(' '),
  );

  public dataCy = computed(() => {
    if (this.id()) {
      return this.id();
    }
    return `ps-btn-${this.variant()}-${this.color()}-${this.size()}`;
  });
}
